import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';

import type { RegionDetail, RegionNode } from '../../../../api/regions';
import { SectionCard } from '../../regional/BucketStates';

import { RegionMoveRow } from './RegionMoveRow';
import { flattenById } from './regionRoot';
import { useDeleteRegion, useMergeRegion } from './regionsQueries';

interface Props {
  detail: RegionDetail;
  regions: RegionNode[];
  /** Navigate away after the region is merged or deleted. */
  onGone: () => void;
}

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '12px 0',
};
const selectStyle: CSSProperties = {
  height: 28,
  borderRadius: 7,
  border: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
  font: 'inherit',
  fontSize: 12,
  color: 'var(--nb-ink)',
};

/** One labelled danger action: title + note on the left, control on the right. */
function DangerRow({
  title,
  note,
  control,
  divider,
}: {
  title: string;
  note: string;
  control: ReactNode;
  divider?: boolean;
}) {
  return (
    <div style={{ ...rowStyle, borderBottom: divider ? '1px solid var(--nb-border)' : undefined }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nb-ink)' }}>{title}</div>
        <div style={{ fontSize: 11.5, color: 'var(--nb-subtle)' }}>{note}</div>
      </div>
      {control}
    </div>
  );
}

/** Merge this region into another, or hard-delete it when it holds no venues. */
export function RegionDangerZone({ detail, regions, onGone }: Props) {
  const [target, setTarget] = useState('');
  const merge = useMergeRegion();
  const del = useDeleteRegion();

  const targets = [...flattenById(regions).values()].filter((r) => r.id !== detail.id);
  const canDelete = detail.venueCount === 0 && detail.subregionCount === 0;
  const deleteNote = canDelete
    ? 'This region has no venues or subregions and can be permanently deleted.'
    : `Can’t delete — ${detail.venueCount} venue(s) and ${detail.subregionCount} subregion(s) are still assigned. Reassign or merge them first.`;

  return (
    <SectionCard title="Danger zone">
      {/* Move first: it is the reversible one, and the only way to restructure
          the hierarchy since drag-to-reparent was removed (BON-423/BON-448). */}
      <RegionMoveRow detail={detail} regions={regions} divider />
      <DangerRow
        divider
        title="Merge into another region"
        note="Moves all venues and subregions to the chosen region, then archives this one."
        control={
          <>
            <select
              aria-label="Merge target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              style={selectStyle}
            >
              <option value="">Choose region…</option>
              {targets.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="nb-btn"
              style={{ height: 28, fontSize: 12 }}
              disabled={!target || merge.isPending}
              onClick={() =>
                merge.mutate({ id: detail.id, intoRegionId: target }, { onSuccess: onGone })
              }
            >
              {merge.isPending ? 'Merging…' : 'Merge…'}
            </button>
          </>
        }
      />
      <DangerRow
        title="Delete region"
        note={deleteNote}
        control={
          <button
            type="button"
            className="nb-btn"
            style={{
              height: 28,
              fontSize: 12,
              color: 'var(--nb-risk)',
              borderColor: 'var(--nb-risk)',
            }}
            disabled={!canDelete || del.isPending}
            onClick={() => del.mutate(detail.id, { onSuccess: onGone })}
          >
            {del.isPending ? 'Deleting…' : 'Delete'}
          </button>
        }
      />
    </SectionCard>
  );
}
